"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type BondResultStrings,
  BondResults,
  type EnergyUnit,
} from "@/components/tools/chem-reference/bond-results";
import {
  BondTable,
  type BondTableStrings,
} from "@/components/tools/chem-reference/bond-table";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { Bond } from "@/lib/tool-kit/chem-reference/bond-data";
import {
  addBond,
  BOND_PRESETS,
  bondEnthalpy,
  type ParseProblem,
  parseBondList,
} from "@/lib/tool-kit/chem-reference/bond-enthalpy";

/** English strings. A translated content file overrides these through `ui`. */
export const BOND_ENTHALPY_CALCULATOR_STRINGS = {
  brokenLabel: "Bonds broken (in the reactants)",
  formedLabel: "Bonds formed (in the products)",
  listHint:
    "Count a bond for every molecule, coefficients included: 4 C–H, 2 O=O. Use - for single, = for double and ≡ (or #) for triple bonds. Add @799 to a bond to use your own energy.",
  presetLabel: "Try a reaction",
  unitLabel: "Show ΔH in",
  unitNames: { kJ: "kJ/mol", kcal: "kcal/mol" },
  results: "Results",
  enterPrompt: "List the bonds broken and formed to estimate ΔH.",
  problemsHeading: "Some entries could not be read:",
  problemMessages: {
    syntax:
      "“{token}” is not a bond. Write two element symbols and a bond symbol, such as C–H or O=O.",
    unknown:
      "“{token}” is not in the bond table. Check the elements and the bond order.",
    count: "“{token}” has a count outside 0–1000.",
    energy: "“{token}” has an energy that is not above zero.",
  },
  deltaH: "Estimated ΔH",
  heat: "Heat flow",
  heatNames: {
    exothermic: "Exothermic (ΔH < 0)",
    endothermic: "Endothermic (ΔH > 0)",
    none: "No net change",
  },
  brokenTotal: "Energy to break bonds",
  formedTotal: "Energy released forming bonds",
  brokenHeading: "Bonds broken",
  formedHeading: "Bonds formed",
  columnBond: "Bond",
  columnCount: "Number",
  columnEnergy: "Energy (kJ/mol)",
  columnSubtotal: "Subtotal (kJ/mol)",
  customNote: "* marks an energy you set with @; the table value was not used.",
  working: "The working",
  workBroken: "Σ broken = {sum} = {total}",
  workFormed: "Σ formed = {sum} = {total}",
  workDelta: "ΔH ≈ Σ broken − Σ formed = {broken} − {formed} = {delta}",
  estimateNote:
    "A rough estimate for gas-phase reactions: average bond energies ignore the surroundings of each bond, so expect differences of tens of kJ/mol from a calculation with enthalpies of formation.",
  tableHeading: "Average bond energies",
  filterLabel: "Filter the bond table",
  filterPlaceholder: "C, O=O, H–Cl",
  columnAdd: "Add to the lists",
  addBroken: "+ broken",
  addFormed: "+ formed",
  addBrokenLabel: "Add {bond} to the bonds broken",
  addFormedLabel: "Add {bond} to the bonds formed",
  noBonds: "No bond matches that filter.",
  tableSource:
    "Source: OpenStax Chemistry 2e, section 7.5, Table 7.2, average bond energies for gaseous molecules.",
  copy: "Copy result",
  copied: "Copied!",
  copyText: "ΔH ≈ {delta} ({heat}), from average bond energies",
} as const;

type Strings = typeof BOND_ENTHALPY_CALCULATOR_STRINGS;

const UNITS: readonly EnergyUnit[] = ["kJ", "kcal"];

function Problems({
  problems,
  strings,
}: {
  problems: readonly ParseProblem[];
  strings: Strings;
}) {
  if (problems.length === 0) {
    return null;
  }
  return (
    <ErrorNotice>
      {strings.problemsHeading}{" "}
      {problems
        .map((p) =>
          t(strings.problemMessages[p.reason], { token: p.token.trim() })
        )
        .join(" ")}
    </ErrorNotice>
  );
}

export default function BondEnthalpyCalculatorWidget() {
  const strings = useToolUi(BOND_ENTHALPY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [brokenText, setBrokenText] = useState(BOND_PRESETS[0].broken);
  const [formedText, setFormedText] = useState(BOND_PRESETS[0].formed);
  const [unit, setUnit] = useState<EnergyUnit>("kJ");

  const broken = parseBondList(brokenText);
  const formed = parseBondList(formedText);
  const empty = broken.entries.length + formed.entries.length === 0;
  const problems = [...broken.problems, ...formed.problems];

  function add(bond: Bond, side: "broken" | "formed") {
    if (side === "broken") {
      setBrokenText(addBond(brokenText, bond));
    } else {
      setFormedText(addBond(formedText, bond));
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.presetLabel}</span>
        {BOND_PRESETS.map((preset) => (
          <button
            className={BUTTON_SMALL}
            key={preset.id}
            onClick={() => {
              setBrokenText(preset.broken);
              setFormedText(preset.formed);
            }}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextAreaField
          hint={strings.listHint}
          id="be-broken"
          label={strings.brokenLabel}
          onChange={setBrokenText}
          rows={3}
          value={brokenText}
        />
        <TextAreaField
          id="be-formed"
          label={strings.formedLabel}
          onChange={setFormedText}
          rows={3}
          value={formedText}
        />
      </div>
      <PillGroup
        label={strings.unitLabel}
        name="be-unit"
        onChange={setUnit}
        options={UNITS.map((value) => ({
          value,
          label: strings.unitNames[value],
        }))}
        value={unit}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Problems problems={problems} strings={strings} />
        {empty ? (
          <Notice>{strings.enterPrompt}</Notice>
        ) : (
          <BondResults
            broken={broken.entries}
            fmt={fmt}
            formed={formed.entries}
            result={bondEnthalpy(broken.entries, formed.entries)}
            strings={strings as BondResultStrings}
            unit={unit}
          />
        )}
      </section>
      <BondTable fmt={fmt} onAdd={add} strings={strings as BondTableStrings} />
    </>
  );
}
